import {
  WSAlertProps,
  WSDataItemProps,
  WSElementColorsType,
  WSPageToolbarAction,
  WSPillProps,
  WSTableCell,
  WSTableCellProps,
  WSTableRowMenuAction
} from "@wingspanhq/fe-component-library";
import {
  BackgroundCheck,
  BiometricIdentityVerificationRequest,
  InsuranceMonitor,
  PayeeRequirement,
  RequirementStatus,
  RequirementType,
  SharedFileRequest,
  SignatureRequest
} from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { REQUIREMENT_TYPE_EMPLOYEE_TAX_FORMS } from "./modules/EmployeeTaxForms/constants";
import { DataViewProps } from "../../shared/components/DataView";
import { StepItem } from "../../shared/components/Steps";

import { TaxVerificationAdditionalData } from "./modules/TaxVerification/types";
import { RequirementEvent } from "./utils";

export enum RequirementContextType {
  Payer = "Payer",
  Payee = "Payee"
}

/**
 * The requirement types this module renders. The SDK enum has 15 members;
 * the other 7 (Insurance, License, Custom, Acknowledgement, SignatureRequest,
 * ExternalVendor, ExternalCompletion) have no UI here yet — instances of
 * those types are filtered out via `isSupportedRequirementType`.
 *
 * EmployeeTaxForms is a v3 wire value the installed SDK enum predates — it
 * joins the union as a literal (see modules/EmployeeTaxForms/constants.ts),
 * keeping every SupportedRequirementType-keyed Record narrow.
 */
export const SUPPORTED_REQUIREMENT_TYPES = [
  RequirementType.SIGNATURE,
  RequirementType.DOCUMENT_UPLOAD,
  RequirementType.BACKGROUND_CHECK,
  RequirementType.BIOMETRIC_IDENTITY_VERIFICATION,
  RequirementType.INSURANCE_COVERAGE,
  RequirementType.REGISTRATION,
  RequirementType.PAYOUT_METHOD,
  RequirementType.TAX_VERIFICATION,
  REQUIREMENT_TYPE_EMPLOYEE_TAX_FORMS
] as const;

export type SupportedRequirementType =
  (typeof SUPPORTED_REQUIREMENT_TYPES)[number];

export const isSupportedRequirementType = (
  type: RequirementType | string | undefined
): type is SupportedRequirementType =>
  !!type && (SUPPORTED_REQUIREMENT_TYPES as readonly string[]).includes(type);

export type RequirementTableItemData = PayeeRequirement;

export type ExtendedRequirementTableItemData =
  | PayeeRequirement
  | CustomRequirement;

export type CustomRequirement = {
  status: RequirementStatus;
  payerAccountId: string;
  payeeEngagementIds?: string[];

  nameCell: WSTableCellProps;
  typeCell: WSTableCellProps;
  clientsCell?: WSTableCellProps;
  engagementsCell: WSTableCellProps;
  statusCell: WSTableCellProps;
  menuActions?: WSTableRowMenuAction[];
};

export type RequirementAdditionalData =
  | BackgroundCheck
  | SharedFileRequest
  | SignatureRequest
  | BiometricIdentityVerificationRequest
  | InsuranceMonitor
  | TaxVerificationAdditionalData
  | undefined;

type DataItem<
  T extends RequirementType | typeof REQUIREMENT_TYPE_EMPLOYEE_TAX_FORMS
> = PayeeRequirement;

export type ContextItem = {
  hideTaxVerificationConsent?: boolean;
};

export type TableCellProps = React.ComponentProps<typeof WSTableCell>;

export type RenderCellFunction<
  T extends RequirementType | typeof REQUIREMENT_TYPE_EMPLOYEE_TAX_FORMS
> = (dataItem: DataItem<T>, context: ContextItem) => TableCellProps;

export type CellsByContext<
  T extends RequirementType | typeof REQUIREMENT_TYPE_EMPLOYEE_TAX_FORMS
> = {
  [keyof in RequirementContextType]: RenderCellFunction<T>;
};

export type RequirementAction = WSPageToolbarAction & {
  noTable?: boolean;
  noOnboarding?: boolean;
  mainAction?: boolean;
};

export type RequirementFieldsConfig<
  T extends RequirementType | typeof REQUIREMENT_TYPE_EMPLOYEE_TAX_FORMS
> = {
  requirementDetailsWidgets?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => React.ReactNode;
  };
  isActionRequired?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => boolean;
  };
  requirementDetailsProgressWidget?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => StepItem[] | undefined;
  };
  requirementInstructionsWidgets?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => React.ReactNode;
  };
  requirementInstructions?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => (instructions: string) => React.ReactNode;
  };
  requirementName?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => string;
  };
  toolbarActions?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => RequirementAction[];
  };
  requirementStatusAlert?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => WSAlertProps | undefined;
  };
  requirementBasicInformation?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => WSDataItemProps[];
  };
  requirementDetails?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => DataViewProps | React.ReactNode;
  };
  requirementAdditionalContent?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => React.ReactNode;
  };
  nameCell: CellsByContext<T>;
  typeCell: CellsByContext<T>;
  statusCell: CellsByContext<T>;
  clientsCell?: CellsByContext<T>;
  engagementsCell?: CellsByContext<T>;
  getAdditionalData(
    dataItem: DataItem<T>,
    contextItem: ContextItem
  ): RequirementAdditionalData;
  /** No document yet — the very first read of this type's data source. */
  isLoading: boolean;
  /**
   * A read is in flight while a document is already cached — i.e. a refresh
   * after an invalidation. Distinct from `isLoading`, which React Query only
   * reports when there is nothing to render at all; without this the UI shows
   * stale post-mutation data with no indication it is being replaced.
   */
  isFetching: boolean;
  dataUpdatedAt: string;
};

type FieldConfigFunction<
  RequirementData extends RequirementTableItemData,
  ContextData extends ContextItem,
  AdditionalData,
  Result
> = (
  dataItem: RequirementData,
  contextItem: ContextData,
  additionalData: AdditionalData
) => Result;

export type CellsConfig<
  R extends RequirementTableItemData,
  C extends ContextItem,
  A
> = {
  isActionRequired: FieldConfigFunction<R, C, A, boolean>;
  requirementName?: FieldConfigFunction<R, C, A, string>;
  requirementDetailsWidgets?: FieldConfigFunction<R, C, A, React.ReactNode>;
  requirementDetailsProgressWidget?: FieldConfigFunction<
    R,
    C,
    A,
    StepItem[] | undefined
  >;
  requirementInstructions?: FieldConfigFunction<
    R,
    C,
    A,
    (instructions: string) => React.ReactNode
  >;
  requirementInstructionsWidgets?: FieldConfigFunction<
    R,
    C,
    A,
    React.ReactNode
  >;
  requirementBasicInformation?: FieldConfigFunction<R, C, A, WSDataItemProps[]>;
  requirementDetails?: FieldConfigFunction<
    R,
    C,
    A,
    DataViewProps | React.ReactNode
  >;
  requirementAdditionalContent?: FieldConfigFunction<R, C, A, React.ReactNode>;
  toolbarActions?: FieldConfigFunction<R, C, A, RequirementAction[]>;
  requirementStatusAlert?: FieldConfigFunction<
    R,
    C,
    A,
    (WSAlertProps & { children?: React.ReactNode }) | undefined
  >;
  nameCell: FieldConfigFunction<R, C, A, TableCellProps>;
  typeCell: FieldConfigFunction<R, C, A, TableCellProps>;
  statusCell: FieldConfigFunction<R, C, A, TableCellProps>;
  clientsCell?: FieldConfigFunction<R, C, A, TableCellProps>;
  engagementsCell?: FieldConfigFunction<R, C, A, TableCellProps>;
};

export type RequirementTableRowConfig = {
  [K in SupportedRequirementType]: RequirementFieldsConfig<K>;
};

export type RequirementTableCallback = (
  eventName: RequirementEvent,
  item: RequirementTableItemData
) => void;

export type RequirementStatusViewProps = {
  pill: WSPillProps;
  secondaryText?: string;
  secondaryTextColor?: WSElementColorsType;
};

export type RequirementDetailsAlertProps =
  | (WSAlertProps & {
      children?: React.ReactNode;
    })
  | undefined;
